import { Button } from '@/components/ui/Button';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarPlus, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { LeaveDayTrace } from '../components/LeaveDayTrace';
import { LeaveRequestRow } from '../components/LeaveRequestRow';
import { SirhPageHeader } from '../components/SirhPageHeader';
import { sirhService } from '../services/sirh.service';
import { LEAVE_STATE_LABELS, type LeaveState } from '../types';

const FILTERS: { value: 'all' | LeaveState; label: string }[] = [
  { value: 'all', label: 'Toutes' },
  { value: 'submitted', label: 'À approuver' },
  { value: 'applied', label: 'Décomptées' },
  { value: 'rejected', label: 'Refusées' },
];

/**
 * Écran salarié — « Mes absences ».
 *
 * La tâche principale est évidente en arrivant : poser une absence. Le solde
 * tient sur une ligne fine, pas sur des cartes en en-tête, pour que la liste
 * commence haut. Le nombre de jours affiché pendant la saisie vient du serveur :
 * c'est déjà celui qui sera enregistré, et sa trace jour par jour est visible
 * avant même de valider.
 */
export function MyLeavePage() {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<'all' | LeaveState>('all');
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);
  const [isComposing, setIsComposing] = useState(false);
  const [confirmCancelUuid, setConfirmCancelUuid] = useState<string | null>(
    null
  );
  const [draft, setDraft] = useState({
    startsOn: '',
    endsOn: '',
    leaveTypeUuid: '',
    startBoundary: 'journee' as 'journee' | 'apres_midi',
    endBoundary: 'journee' as 'journee' | 'matin',
    employeeComment: '',
    justificationReference: '',
  });

  const { data: me } = useQuery({
    queryKey: ['sirh', 'me'],
    queryFn: () => sirhService.me(),
    retry: false,
  });
  const employmentUuid = me?.ownEmploymentUuids?.[0];

  const { data: overview, isLoading } = useQuery({
    queryKey: ['sirh', 'mine'],
    queryFn: () => sirhService.mine(),
    retry: false,
  });

  const requests = useMemo(
    () => overview?.requests ?? [],
    [overview?.requests]
  );
  const leaveTypes = useMemo(
    () => overview?.leaveTypes ?? [],
    [overview?.leaveTypes]
  );
  const selectedDraftType = useMemo(
    () => leaveTypes.find(type => type.uuid === draft.leaveTypeUuid),
    [draft.leaveTypeUuid, leaveTypes]
  );

  const { data: stateMachine } = useQuery({
    queryKey: ['sirh', 'state-machine'],
    queryFn: () => sirhService.stateMachine(),
  });

  useEffect(() => {
    if (!draft.leaveTypeUuid && leaveTypes.length > 0) {
      setDraft(current => ({ ...current, leaveTypeUuid: leaveTypes[0].uuid }));
    }
  }, [leaveTypes, draft.leaveTypeUuid]);

  // Décompte en direct, calculé par le serveur pendant la saisie.
  const { data: preview } = useQuery({
    queryKey: [
      'sirh',
      'preview',
      employmentUuid,
      draft.startsOn,
      draft.endsOn,
      draft.leaveTypeUuid,
      draft.startBoundary,
      draft.endBoundary,
    ],
    queryFn: () =>
      sirhService.previewLeave({
        employmentUuid: employmentUuid as string,
        leaveTypeUuid: draft.leaveTypeUuid,
        startsOn: draft.startsOn,
        endsOn: draft.endsOn,
        startBoundary: draft.startBoundary,
        endBoundary: draft.endBoundary,
      }),
    enabled: Boolean(
      employmentUuid && draft.startsOn && draft.endsOn && draft.leaveTypeUuid
    ),
    retry: false,
  });

  const create = useMutation({
    mutationFn: () =>
      sirhService.createLeave({
        employmentUuid: employmentUuid as string,
        leaveTypeUuid: draft.leaveTypeUuid,
        startsOn: draft.startsOn,
        endsOn: draft.endsOn,
        startBoundary: draft.startBoundary,
        endBoundary: draft.endBoundary,
        employeeComment: draft.employeeComment.trim() || undefined,
        justificationReference:
          draft.justificationReference.trim() || undefined,
      }),
    onSuccess: async created => {
      await sirhService.submitLeave(created.uuid, created.version);
      setIsComposing(false);
      setDraft(current => ({
        ...current,
        startsOn: '',
        endsOn: '',
        startBoundary: 'journee',
        endBoundary: 'journee',
        employeeComment: '',
        justificationReference: '',
      }));
      void queryClient.invalidateQueries({ queryKey: ['sirh', 'mine'] });
    },
  });

  const cancel = useMutation({
    mutationFn: (request: { uuid: string; version: number }) =>
      sirhService.cancelLeave(request.uuid, request.version),
    onSuccess: updated => {
      setConfirmCancelUuid(null);
      setSelectedUuid(updated.uuid);
      void queryClient.invalidateQueries({ queryKey: ['sirh', 'mine'] });
    },
  });

  /** Solde de la période de droits en cours : acquis moins consommés. */
  const balance = useMemo(() => {
    const entitlement = overview?.entitlements?.find(
      item =>
        item.employmentUuid === employmentUuid &&
        item.leaveTypeUuid === draft.leaveTypeUuid
    );
    if (!entitlement) {
      return null;
    }
    const type = leaveTypes.find(
      item => item.uuid === entitlement.leaveTypeUuid
    );
    return {
      label: type?.label ?? 'Absence',
      periodStart: entitlement.periodStart,
      periodEnd: entitlement.periodEnd,
      remaining:
        Number(entitlement.acquiredDays) - Number(entitlement.takenDays),
      acquired: Number(entitlement.acquiredDays),
      taken: Number(entitlement.takenDays),
    };
  }, [draft.leaveTypeUuid, employmentUuid, leaveTypes, overview?.entitlements]);

  const visible = useMemo(
    () =>
      filter === 'all'
        ? requests
        : requests.filter(request => request.state === filter),
    [requests, filter]
  );
  const selected =
    visible.find(request => request.uuid === selectedUuid) ?? null;
  const selectedType = selected
    ? leaveTypes.find(type => type.uuid === selected.leaveTypeUuid)
    : null;
  const canCancel = Boolean(
    selected &&
      stateMachine?.transitions.some(
        transition =>
          transition.from === selected.state && transition.command === 'cancel'
      )
  );

  return (
    <div className="space-y-6">
      <SirhPageHeader
        title="Mes absences"
        subtitle="Les jours sont décomptés par le serveur, selon la politique en vigueur à la date de l’absence."
        actions={
          <Button
            variant="brand"
            onClick={() => setIsComposing(value => !value)}
            className="gap-2"
          >
            <CalendarPlus className="h-4 w-4" />
            Poser une absence
          </Button>
        }
      />

      {balance && (
        <p className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-sm text-neutral-500">
          <span>
            <span className="text-base font-semibold text-neutral-900">
              {balance.remaining.toLocaleString('fr-FR')}
            </span>{' '}
            jour{balance.remaining > 1 ? 's' : ''} disponible
            {balance.remaining > 1 ? 's' : ''}
          </span>
          <span>
            {balance.acquired.toLocaleString('fr-FR')} acquis ·{' '}
            {balance.taken.toLocaleString('fr-FR')} pris
          </span>
          <span>
            {balance.label} · période du {balance.periodStart} au{' '}
            {balance.periodEnd}
          </span>
        </p>
      )}

      {isComposing && (
        <section className="rounded-xl border border-neutral-200 bg-white p-6">
          <div className="grid gap-4 sm:grid-cols-4">
            <label className="block">
              <span className="text-sm font-medium text-neutral-700">
                Type d’absence
              </span>
              <select
                value={draft.leaveTypeUuid}
                onChange={event =>
                  setDraft(current => ({
                    ...current,
                    leaveTypeUuid: event.target.value,
                    justificationReference: '',
                  }))
                }
                className="mt-1 h-10 w-full rounded-xl border border-neutral-300 px-3 text-sm"
              >
                {leaveTypes.map(type => (
                  <option key={type.uuid} value={type.uuid}>
                    {type.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-sm font-medium text-neutral-700">
                Premier jour
              </span>
              <input
                type="date"
                value={draft.startsOn}
                onChange={event =>
                  setDraft(current => ({
                    ...current,
                    startsOn: event.target.value,
                  }))
                }
                className="mt-1 h-10 w-full rounded-xl border border-neutral-300 px-3 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-neutral-700">
                Dernier jour
              </span>
              <input
                type="date"
                value={draft.endsOn}
                onChange={event =>
                  setDraft(current => ({
                    ...current,
                    endsOn: event.target.value,
                  }))
                }
                className="mt-1 h-10 w-full rounded-xl border border-neutral-300 px-3 text-sm"
              />
            </label>
            <div className="flex items-end">
              <Button
                variant="brand"
                className="w-full"
                disabled={
                  !preview ||
                  create.isPending ||
                  Boolean(
                    selectedDraftType?.requiresJustification &&
                      !draft.justificationReference.trim()
                  )
                }
                onClick={() => create.mutate()}
              >
                {preview
                  ? `Demander ${preview.totalDays.toLocaleString('fr-FR')} jour${preview.totalDays > 1 ? 's' : ''}`
                  : 'Choisir des dates'}
              </Button>
            </div>
          </div>

          {selectedDraftType?.requiresJustification && (
            <div className="mt-5 max-w-xl">
              <label
                htmlFor="leave-justification-reference"
                className="block text-sm font-medium text-neutral-700"
              >
                Référence du justificatif
              </label>
              <input
                id="leave-justification-reference"
                type="text"
                required
                aria-describedby="leave-justification-help"
                maxLength={240}
                value={draft.justificationReference}
                onChange={event =>
                  setDraft(current => ({
                    ...current,
                    justificationReference: event.target.value,
                  }))
                }
                placeholder="Référence interne ou nom du document"
                className="mt-1 h-10 w-full rounded-xl border border-neutral-300 px-3 text-sm placeholder:text-neutral-400/50"
              />
              <p
                id="leave-justification-help"
                className="mt-1 text-xs text-neutral-500"
              >
                Ce type d’absence exige un justificatif. Indiquez la référence
                du document transmis à votre employeur.
              </p>
            </div>
          )}

          {preview && (
            <div className="mt-6 border-t border-neutral-100 pt-6">
              <LeaveDayTrace
                days={preview.days}
                total={preview.totalDays}
                countingMode={preview.countingMode}
                notes={preview.notes}
              />
            </div>
          )}

          <details className="mt-5 border-t border-neutral-100 pt-4">
            <summary className="cursor-pointer text-sm font-medium text-neutral-700 hover:text-neutral-950">
              Ajouter des précisions
            </summary>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              {draft.startsOn && draft.startsOn === draft.endsOn ? (
                <label className="block sm:col-span-2 sm:max-w-sm">
                  <span className="text-sm font-medium text-neutral-700">
                    Durée
                  </span>
                  <select
                    value={
                      draft.startBoundary === 'apres_midi'
                        ? 'apres_midi'
                        : draft.endBoundary === 'matin'
                          ? 'matin'
                          : 'journee'
                    }
                    onChange={event => {
                      const duration = event.target.value;
                      setDraft(current => ({
                        ...current,
                        startBoundary:
                          duration === 'apres_midi' ? 'apres_midi' : 'journee',
                        endBoundary: duration === 'matin' ? 'matin' : 'journee',
                      }));
                    }}
                    className="mt-1 h-10 w-full rounded-xl border border-neutral-300 px-3 text-sm"
                  >
                    <option value="journee">Journée entière</option>
                    <option value="matin">Matin</option>
                    <option value="apres_midi">Après-midi</option>
                  </select>
                </label>
              ) : (
                <>
                  <label className="block">
                    <span className="text-sm font-medium text-neutral-700">
                      Début de l’absence
                    </span>
                    <select
                      value={draft.startBoundary}
                      onChange={event =>
                        setDraft(current => ({
                          ...current,
                          startBoundary: event.target.value as
                            | 'journee'
                            | 'apres_midi',
                        }))
                      }
                      className="mt-1 h-10 w-full rounded-xl border border-neutral-300 px-3 text-sm"
                    >
                      <option value="journee">Journée entière</option>
                      <option value="apres_midi">Après-midi</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="text-sm font-medium text-neutral-700">
                      Fin de l’absence
                    </span>
                    <select
                      value={draft.endBoundary}
                      onChange={event =>
                        setDraft(current => ({
                          ...current,
                          endBoundary: event.target.value as
                            | 'journee'
                            | 'matin',
                        }))
                      }
                      className="mt-1 h-10 w-full rounded-xl border border-neutral-300 px-3 text-sm"
                    >
                      <option value="journee">Journée entière</option>
                      <option value="matin">Matin</option>
                    </select>
                  </label>
                </>
              )}
              <label className="block sm:col-span-2">
                <span className="text-sm font-medium text-neutral-700">
                  Commentaire facultatif
                </span>
                <textarea
                  rows={3}
                  maxLength={2_000}
                  value={draft.employeeComment}
                  onChange={event =>
                    setDraft(current => ({
                      ...current,
                      employeeComment: event.target.value,
                    }))
                  }
                  placeholder="Ajoutez uniquement l’information utile à votre responsable"
                  className="mt-1 w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm placeholder:text-neutral-400/50"
                />
              </label>
            </div>
          </details>

          {create.isError && (
            <p className="text-error-700 mt-4 text-sm" role="alert">
              La demande n’a pas pu être enregistrée. Vérifiez les dates et le
              solde, puis réessayez.
            </p>
          )}
        </section>
      )}

      <div className="flex flex-wrap items-center gap-4">
        {FILTERS.map(item => (
          <button
            key={item.value}
            type="button"
            onClick={() => setFilter(item.value)}
            className={
              filter === item.value
                ? 'rounded-full bg-neutral-900 px-3 py-2 text-xs font-medium text-white'
                : 'rounded-full bg-neutral-100 px-3 py-2 text-xs font-medium text-neutral-600 hover:bg-neutral-200'
            }
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <section className="space-y-3 rounded-xl border border-neutral-200 bg-white p-3">
          {isLoading && (
            <p className="px-4 py-8 text-sm text-neutral-500">Chargement…</p>
          )}
          {!isLoading && visible.length === 0 && (
            <p className="px-4 py-8 text-sm text-neutral-500">
              Aucune absence sur ce filtre. Le bouton « Poser une absence »
              ouvre la saisie.
            </p>
          )}
          {visible.map(request => (
            <LeaveRequestRow
              key={request.uuid}
              request={request}
              selected={request.uuid === selectedUuid}
              onSelect={() => setSelectedUuid(request.uuid)}
            />
          ))}
        </section>

        {selected && (
          <aside className="rounded-xl border border-neutral-200 bg-white p-6">
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-neutral-900">
                  {LEAVE_STATE_LABELS[selected.state]}
                </p>
                <p className="text-xs text-neutral-500">
                  Politique v{selected.policyVersion} · jours{' '}
                  {selected.countingMode}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedUuid(null)}
                className="rounded-lg p-2 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
                aria-label="Fermer le détail"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-sm text-neutral-700">
              {Number(selected.countedDays).toLocaleString('fr-FR')} jour
              {Number(selected.countedDays) > 1 ? 's' : ''} du{' '}
              {selected.startsOn} au {selected.endsOn}
            </p>
            {selectedType && (
              <p className="mt-1 text-sm text-neutral-500">
                {selectedType.label}
              </p>
            )}
            {selected.employeeComment && (
              <div className="mt-4 border-l-2 border-neutral-200 pl-3">
                <p className="text-xs font-medium text-neutral-500">
                  Votre commentaire
                </p>
                <p className="mt-1 text-sm whitespace-pre-wrap text-neutral-700">
                  {selected.employeeComment}
                </p>
              </div>
            )}
            {selected.justificationReference && (
              <div className="mt-4 border-l-2 border-neutral-200 pl-3">
                <p className="text-xs font-medium text-neutral-500">
                  Référence du justificatif
                </p>
                <p className="mt-1 text-sm text-neutral-700">
                  {selected.justificationReference}
                </p>
              </div>
            )}
            {selected.computationNotes &&
              selected.computationNotes.length > 0 && (
                <ul className="mt-4 space-y-1 text-sm text-neutral-500">
                  {selected.computationNotes.map(note => (
                    <li key={note}>{note}</li>
                  ))}
                </ul>
              )}

            {canCancel && confirmCancelUuid !== selected.uuid && (
              <button
                type="button"
                onClick={() => setConfirmCancelUuid(selected.uuid)}
                className="mt-5 min-h-10 rounded-lg px-3 text-sm font-medium text-neutral-600 hover:bg-neutral-100 hover:text-neutral-950"
              >
                Annuler la demande
              </button>
            )}

            {canCancel && confirmCancelUuid === selected.uuid && (
              <div className="border-error-300 mt-5 border-l-2 pl-4">
                <p className="text-sm text-neutral-700">
                  Confirmer l’annulation de cette demande ?
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={cancel.isPending}
                    onClick={() =>
                      cancel.mutate({
                        uuid: selected.uuid,
                        version: selected.version,
                      })
                    }
                  >
                    {cancel.isPending ? 'Annulation…' : 'Confirmer'}
                  </Button>
                  <button
                    type="button"
                    disabled={cancel.isPending}
                    onClick={() => setConfirmCancelUuid(null)}
                    className="min-h-9 rounded-lg px-3 text-sm text-neutral-600 hover:bg-neutral-100"
                  >
                    Retour
                  </button>
                </div>
              </div>
            )}

            {cancel.isError && (
              <p className="text-error-700 mt-4 text-sm" role="alert">
                La demande a changé ou ne peut plus être annulée. Rechargez la
                liste.
              </p>
            )}
          </aside>
        )}
      </div>
    </div>
  );
}
